import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {hpcAttrs} from '@github-ui/hpc'
import {addIssueFieldToQuery, createReplaceIssueFieldQuery} from '@github-ui/issue-url-helper'
import type {
  RepoIssuesDashboardPageInfo,
  RepoIssuesDashboardResult,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Link as PreloadableLink, Link as RouterLink} from '@github-ui/react-router'
import {IssuesList} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import type {IssueDeferredItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import type {DeferredItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import type {EmptyListState} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'
import type {FilterSelectType} from '@github-ui/shared-dashboard-lists/shared/types/list-types'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addBlockedFilterToQuery,
  addLabelFilterToQuery,
  addParentIssueFilterToQuery,
  addRepoFilterToQuery,
  addTypeFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {addStateToQuery, getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {
  addIssueStateToQuery,
  getActiveIssueFilterQuery,
  getIssueStateFromQuery,
  isActiveIssueFilterAvailable,
} from '@github-ui/issues-shared/issue-state-filter'
import type {IssueStatusSearchOptions} from '@github-ui/shared-filter-providers/providers/issue-status'
import {Pagination} from '@primer/react'
import {type ComponentProps, type ReactNode, useMemo, useState} from 'react'

import type {DashboardListContainerProps} from '../hooks/use-dashboard-issue-side-panel'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'

type IssuesListProps = ComponentProps<typeof IssuesList>
export type IssuesListSecondaryResult = IssueDeferredItem | (DeferredItem & {itemType: 'pull_request'})
const EMPTY_SECONDARY_RESULTS: IssuesListSecondaryResult[] = []
export const ISSUES_RESULTS_SPACING_STYLE = {
  marginBlockStart: 'var(--base-size-16)',
  marginBlockEnd: 'var(--base-size-16)',
} as const

export interface IssuesListRouteAdapter {
  readonly scopeIdentity: string
  readonly query: string
  readonly page: number
  readonly fetch: (input: {query: string; page?: number}) => void
  readonly getPageHref: (page: number) => string
  readonly getQueryHref: (query: string, page?: number) => string
}

export interface IssuesListSurfaceProps {
  readonly listTitle?: IssuesListProps['title']
  readonly response: {
    readonly results?: RepoIssuesDashboardResult[]
    readonly pageInfo?: RepoIssuesDashboardPageInfo
    readonly error?: string
  }
  readonly routeAdapter: IssuesListRouteAdapter
  readonly secondaryResults?: IssuesListSecondaryResult[]
  readonly filterActions?: IssuesListProps['filterActions']
  readonly defaultSortingKey?: IssuesListProps['defaultSortingKey']
  readonly sortingOptions?: IssuesListProps['sortingOptions']
  readonly separateAgentAssignees?: IssuesListProps['separateAgentAssignees']
  readonly isLoading?: boolean
  /** Overrides the rows rendered by the list, e.g. a per-row live-update overlay. */
  readonly issuesOverride?: IssuesListProps['issues']
  /** Rendered just before the list, e.g. Alive live-update subscribers. */
  readonly beforeList?: ReactNode
  /** Controlled compact-mode value; when provided the surface renders it as controlled state. */
  readonly compactMode?: boolean
  /** Called when the density toggle changes; pair with `compactMode` to persist the preference. */
  readonly onCompactModeChange?: (value: boolean) => void
  /** Overrides the computed empty/blankslate state, e.g. one from screen-reader announcements. */
  readonly emptyListStateOverride?: EmptyListState
  /**
   * When provided, clicking an issue row opens it in the shared TanStack IssueViewer overlay
   * instead of navigating. Only issue rows honor this; PR rows always navigate. Pair with
   * `focusShortcutsSuppressed` and `containerProps` from `useDashboardIssueSidePanel`.
   */
  readonly onIssueSelect?: IssuesListProps['onIssueSelect']
  readonly onSelectRow?: IssuesListProps['onSelectRow']
  readonly onIssueNavigate?: IssuesListProps['onIssueNavigate']
  /** True while the issue viewer overlay is open, so the list yields j/k to it. */
  readonly focusShortcutsSuppressed?: boolean
  /** Spread onto the list's container div, e.g. so it can receive focus when the open row is removed. */
  readonly containerProps?: DashboardListContainerProps
  readonly selection?: IssuesListProps['selection']
  /** Milestone Show supplies its own append control; ordinary Issues lists retain numbered links. */
  readonly paginationMode?: 'numbered' | 'none'
  readonly showSorting?: boolean
  readonly showDensityToggle?: boolean
  readonly ordering?: IssuesListProps['ordering']
  readonly issueStatusSearchOptions?: IssueStatusSearchOptions | null
}

export function IssuesListSurface({
  listTitle,
  response,
  routeAdapter,
  secondaryResults = EMPTY_SECONDARY_RESULTS,
  filterActions,
  defaultSortingKey = 'created',
  sortingOptions,
  separateAgentAssignees = false,
  isLoading = false,
  issuesOverride,
  beforeList,
  compactMode: compactModeProp,
  onCompactModeChange,
  emptyListStateOverride,
  onIssueSelect,
  onSelectRow,
  onIssueNavigate,
  focusShortcutsSuppressed,
  containerProps,
  selection,
  paginationMode = 'numbered',
  showSorting = true,
  showDensityToggle = true,
  ordering,
  issueStatusSearchOptions,
}: IssuesListSurfaceProps) {
  const [internalCompactMode, setInternalCompactMode] = useState(false)
  const compactMode = compactModeProp ?? internalCompactMode
  const handleCompactModeChange = onCompactModeChange ?? setInternalCompactMode
  const computedIssues = useMemo(
    () => mergeDeferredIssues(repoIssuesToListItems(response.results ?? []), secondaryResults),
    [response.results, secondaryResults],
  )
  const issues = issuesOverride ?? computedIssues
  const pageInfo = response.pageInfo ?? {currentPage: 1, totalPages: 1, totalCount: 0}
  const activeQuery = isActiveIssueFilterAvailable(issueStatusSearchOptions)
    ? getActiveIssueFilterQuery(routeAdapter.query)
    : undefined
  const canUseActiveFilter = activeQuery !== undefined
  const stateItemSelected = canUseActiveFilter
    ? getIssueStateFromQuery(routeAdapter.query)
    : getStateFromQuery(routeAdapter.query)
  const stateCounts =
    response.pageInfo?.openCount != null && response.pageInfo.closedCount != null
      ? {open: response.pageInfo.openCount, closed: response.pageInfo.closedCount}
      : {
          open: stateItemSelected === 'open' ? pageInfo.totalCount : undefined,
          closed: stateItemSelected === 'closed' ? pageInfo.totalCount : undefined,
        }

  const handleFilterSelect = (type: FilterSelectType, value: string) => {
    switch (type) {
      case 'repo':
        return routeAdapter.fetch({query: addRepoFilterToQuery(routeAdapter.query, value)})
      case 'author':
        return routeAdapter.fetch({query: addAuthorFilterToQuery(routeAdapter.query, value)})
      case 'label':
        return routeAdapter.fetch({query: addLabelFilterToQuery(routeAdapter.query, value)})
      case 'assignee':
        return routeAdapter.fetch({query: addAssigneeFilterToQuery(routeAdapter.query, value)})
      default: {
        const _exhaustive: never = type
        return _exhaustive
      }
    }
  }

  const handleIssueFieldSelect = (fieldName: string, queryValue: string) => {
    const query =
      createReplaceIssueFieldQuery(routeAdapter.query, fieldName, queryValue) ??
      addIssueFieldToQuery(routeAdapter.query, fieldName, queryValue)
    routeAdapter.fetch({query})
  }

  const emptyListState: EmptyListState = emptyListStateOverride ?? {
    dashboardType: 'issues',
    filtered: routeAdapter.query.length > 0,
    currentPage: pageInfo.currentPage,
    pageCount: pageInfo.totalPages,
    errorMessage: response.error || undefined,
  }

  return (
    <div
      style={ISSUES_RESULTS_SPACING_STYLE}
      data-testid="issues-list-surface"
      data-scope-identity={routeAdapter.scopeIdentity}
    >
      <div {...hpcAttrs()} {...containerProps}>
        {beforeList}
        <IssuesList
          title={listTitle}
          issues={issues}
          pageInfo={pageInfo}
          stateCounts={stateCounts}
          compactMode={compactMode}
          onCompactModeChange={handleCompactModeChange}
          defaultSortingKey={defaultSortingKey}
          sortingOptions={sortingOptions}
          showSorting={showSorting}
          showDensityToggle={showDensityToggle}
          separateAgentAssignees={separateAgentAssignees}
          onSortingItemSelect={sort => routeAdapter.fetch({query: addSortToQuery(routeAdapter.query, sort)})}
          onStateFilterSelect={state =>
            routeAdapter.fetch({
              query: canUseActiveFilter
                ? addIssueStateToQuery(routeAdapter.query, state)
                : addStateToQuery(routeAdapter.query, state),
            })
          }
          activeFilter={
            activeQuery !== undefined
              ? {
                  href: routeAdapter.getQueryHref(activeQuery),
                  onSelect: () => routeAdapter.fetch({query: activeQuery}),
                  isSelected: stateItemSelected === 'active',
                  count: response.pageInfo?.activeCount,
                }
              : undefined
          }
          getStateFilterHref={
            canUseActiveFilter
              ? state => routeAdapter.getQueryHref(addIssueStateToQuery(routeAdapter.query, state))
              : undefined
          }
          sortingItemSelected={getSortFromQuery(routeAdapter.query)}
          stateItemSelected={stateItemSelected === 'active' ? undefined : stateItemSelected}
          emptyListState={emptyListState}
          onFilterSelect={handleFilterSelect}
          onIssueFieldSelect={handleIssueFieldSelect}
          filterActions={filterActions}
          isLoading={isLoading}
          isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
          onIssueSelect={onIssueSelect}
          onSelectRow={onSelectRow}
          onIssueNavigate={onIssueNavigate}
          focusShortcutsSuppressed={focusShortcutsSuppressed}
          getIssueTypeHref={issueTypeName =>
            routeAdapter.getQueryHref(addTypeFilterToQuery(routeAdapter.query, issueTypeName))
          }
          getIssueBlockedHref={() => routeAdapter.getQueryHref(addBlockedFilterToQuery(routeAdapter.query))}
          getParentIssueHref={(repoNameWithOwner, number) =>
            routeAdapter.getQueryHref(addParentIssueFilterToQuery(routeAdapter.query, repoNameWithOwner, number))
          }
          issueTypeLinkComponent={RouterLink}
          selection={selection}
          ordering={ordering}
        />
        {paginationMode === 'numbered' && pageInfo.totalPages > 1 && (
          <Pagination
            pageCount={pageInfo.totalPages}
            currentPage={pageInfo.currentPage}
            hrefBuilder={routeAdapter.getPageHref}
            renderPage={({key, number, href, ...props}) => (
              <PreloadableLink key={key} {...props} to={href ?? routeAdapter.getPageHref(number)} preload="intent" />
            )}
            onPageChange={(event, page) => {
              event.preventDefault()
              routeAdapter.fetch({query: routeAdapter.query, page})
            }}
          />
        )}
      </div>
    </div>
  )
}
